// Field media on its way to the file store. Pure helpers shared by the upload queue and tests.
//
// A photo or file is saved on the device first (file://…/media/…) and the doc points at that copy,
// so it shows with no signal. The queue uploads it to orgs/{orgId}/ops/{projectId}/{kind}/{name}
// and then swaps the device path on the doc for the permanent link, so every device and the web
// can show it.

const MIME = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', webp: 'image/webp', gif: 'image/gif',
  pdf: 'application/pdf', m4a: 'audio/mp4', mp4: 'video/mp4', mov: 'video/quicktime',
  dwg: 'application/acad', dxf: 'application/dxf', txt: 'text/plain', csv: 'text/csv',
  doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function extOf(uriOrName = '', mime = '') {
  const clean = String(uriOrName).split(/[?#]/)[0];
  const m = /\.([A-Za-z0-9]{1,5})$/.exec(clean);
  if (m) return m[1].toLowerCase();
  const hit = Object.entries(MIME).find(([, v]) => v === mime);
  return hit ? hit[0] : 'bin';
}

export function mimeOf(ext = '', fallback = 'application/octet-stream') {
  return MIME[String(ext).toLowerCase()] || fallback;
}

export const isImageMime = (mime = '') => String(mime).startsWith('image/');
export const isLocalUri = (u) => typeof u === 'string' && /^(file|content):/.test(u);
export const isRemoteUri = (u) => typeof u === 'string' && /^https?:/.test(u);

const safe = (s) => String(s || '_').replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 80) || '_';

/** Where a file lives in the store. kind: photos | files | voice. */
export function mediaPath({ orgId, projectId, kind = 'photos', name }) {
  if (!orgId || !name) throw new Error('mediaPath: orgId and name required');
  return ['orgs', safe(orgId), 'ops', safe(projectId || '_'), safe(kind), safe(name)].join('/');
}

/**
 * Swap one URI for another wherever it appears in a field value: a string, an array of strings,
 * or objects with a `uri` (project before-photos). Returns the same reference when nothing changed.
 */
export function replaceUri(value, from, to) {
  if (value === from) return to;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((v) => { const n = replaceUri(v, from, to); if (n !== v) changed = true; return n; });
    return changed ? out : value;
  }
  if (value && typeof value === 'object' && value.uri === from) return { ...value, uri: to, localUri: from };
  return value;
}

/** Every device-local URI a doc still points at, for the listed fields. */
export function localUrisIn(data = {}, fields = []) {
  const out = [];
  const walk = (v) => {
    if (isLocalUri(v)) out.push(v);
    else if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object' && isLocalUri(v.uri)) out.push(v.uri);
  };
  fields.forEach((f) => walk(data[f]));
  return [...new Set(out)];
}
